import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Lightbulb, Loader2, Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const TIPS = {
  fraudulent: ["Terms acceptance with IP address and browser at checkout", "Account sign-up and last-active dates showing the cardholder used the pass", "Tests completed and practice answered after the purchase date", "Matching email between account, Stripe receipt and checkout"],
  unrecognized: ["Point out the statement name shows LexVersant / lexversant.ca", "Receipt email and PDF invoice sent at purchase", "Proof the account was used after payment"],
  product_not_received: ["Pass activation date and expiry date", "Every login and practice session after payment", "Receipt email with access instructions", "Tests completed and AI tutor messages sent"],
  product_unacceptable: ["Service description at checkout matches what was delivered", "Practice the student did (shows the product worked)", "No-refund clause in the Terms they accepted", "Any support messages and your replies"],
  duplicate: ["Each charge maps to a separate checkout session and pass", "Different dates or different passes per charge", "If it truly was a duplicate, refund one and accept the dispute"],
  general: ["Terms acceptance record", "Usage after purchase", "Clear refund policy and receipt"],
};
const tipsFor = (r) => TIPS[r] || TIPS.general;

const Row = ({ r }) => {
  const [open, setOpen] = useState(false);
  const decided = r.won + r.lost;
  return (
    <>
      <tr data-testid={`lesson-row-${r.reason}`} className="text-slate-700">
        <td className="px-4 py-2.5 font-medium text-slate-900">{r.label}</td>
        <td className="px-4 py-2.5">{r.total}</td>
        <td className="px-4 py-2.5 text-emerald-700">{r.won}</td>
        <td className="px-4 py-2.5 text-rose-700">{r.lost}</td>
        <td className="px-4 py-2.5">
          {decided ? (
            <div className="flex items-center gap-2"><div className="h-1.5 w-20 overflow-hidden rounded-full bg-rose-100"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${r.win_rate}%` }} /></div><span data-testid={`lesson-rate-${r.reason}`} className="font-semibold">{r.win_rate}%</span></div>
          ) : <span className="text-slate-400">{r.open} open</span>}
        </td>
        <td className="px-4 py-2.5">{money(r.recovered)} <span className="text-xs text-slate-400">/ {money(r.lost_amount)} lost</span></td>
        <td className="px-4 py-2.5"><button data-testid={`lesson-tips-btn-${r.reason}`} onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline">Evidence tips<ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} /></button></td>
      </tr>
      {open && (
        <tr data-testid={`lesson-tips-${r.reason}`}><td colSpan={7} className="bg-indigo-50/60 px-4 py-3">
          <p className="mb-1 flex items-center gap-1.5 text-xs font-bold text-indigo-800"><Lightbulb className="h-3.5 w-3.5" />What wins {r.label.toLowerCase()} disputes</p>
          <ul className="grid gap-1 text-xs text-slate-700 sm:grid-cols-2">{tipsFor(r.reason).map((t) => <li key={t} className="before:mr-1.5 before:text-indigo-500 before:content-['✓']">{t}</li>)}</ul>
        </td></tr>
      )}
    </>
  );
};

export const DisputeLessons = ({ reasons }) => {
  const [advice, setAdvice] = useState("");
  const [busy, setBusy] = useState(false);
  const canAsk = reasons.some((r) => r.won + r.lost > 0);
  const ask = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/stripe-disputes/lessons"); setAdvice(data.advice); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="dispute-lessons" className="mt-4 rounded-xl border border-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <h4 className="text-sm font-semibold text-slate-700">Dispute lessons · by reason (all-time)</h4>
        <button data-testid="dispute-lessons-ai-btn" onClick={ask} disabled={busy || !canAsk} title={canAsk ? "" : "Needs at least one won or lost dispute"} className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-1.5 text-xs font-bold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}{busy ? "Analysing…" : "Get AI advice"}</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm" data-testid="dispute-lessons-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>{["Reason", "Disputes", "Won", "Lost", "Win rate", "Recovered", ""].map((h, i) => <th key={i} className="px-4 py-2 font-semibold">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">{reasons.map((r) => <Row key={r.reason} r={r} />)}</tbody>
        </table>
      </div>
      {advice && <div data-testid="dispute-lessons-advice" className="whitespace-pre-line border-t border-slate-100 bg-amber-50/50 px-4 py-3 text-sm leading-relaxed text-slate-800"><p className="mb-1 flex items-center gap-1.5 text-xs font-bold text-amber-800"><Sparkles className="h-3.5 w-3.5" />AI advice from your dispute history</p>{advice}</div>}
    </div>
  );
};
